// Browser side of web push. The service worker lives at /sw.js (public/).
import { getPushConfig, subscribePush, unsubscribePush } from "./api";

export const pushSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

export const isIos = () =>
  typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent || "");

export const isStandalone = () =>
  typeof window !== "undefined" &&
  ((window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    window.navigator.standalone === true);

function keyToBytes(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = window.atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

async function registration() {
  return navigator.serviceWorker.register("/sw.js");
}

export async function currentSubscription() {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration("/");
  return reg ? reg.pushManager.getSubscription() : null;
}

// Ask permission, subscribe this device and register it with BR OS.
export async function enablePush() {
  if (!pushSupported()) throw new Error("This browser cannot receive notifications.");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Notifications are blocked for BR OS in this browser's settings.");
  const reg = await registration();
  await navigator.serviceWorker.ready;
  const { data } = await getPushConfig();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyToBytes(data.public_key) });
  }
  await subscribePush(sub.toJSON());
  return sub;
}

export async function disablePush() {
  const sub = await currentSubscription();
  if (!sub) return;
  try {
    await unsubscribePush(sub.endpoint);
  } finally {
    await sub.unsubscribe();
  }
}

// On load: if this device already has a subscription, make sure BR OS still
// holds it under the signed-in user (browsers rotate endpoints now and then).
export async function syncPushSubscription() {
  try {
    if (!pushSupported() || Notification.permission !== "granted") return;
    const sub = await currentSubscription();
    if (sub) await subscribePush(sub.toJSON());
  } catch {
    // best effort, the settings dialog shows the real state
  }
}
